/**
 * ACH Payment Form Component
 * Handles bank account payments via ACH
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CoinflowTokenization, COINFLOW_PRODUCT_TYPE, COINFLOW_PRODUCT_DESCRIPTION } from '@/lib/coinflow-tokenization';
import { getDeviceId } from '@/lib/nsure';
import { apiClient } from '@/lib/api-client';
import { Building2, Lock } from 'lucide-react';

interface ACHPaymentFormProps {
  amount: number; // in cents
  packageId: string;
  scheduledOfferId?: string;  // For scheduled offer purchases
  packageTitle: string;
  promoCode?: string;
  promoBonusGC?: number;
  promoBonusSC?: number;
  onSuccess: (paymentId: string, data: { isFirstPurchase?: boolean }) => void;
  onError: (error: string | Error) => void;
  /** Checkout session transaction ID - if provided, reuses this transaction instead of creating a new one */
  checkoutSessionId?: number | null;
}

export const ACHPaymentForm: React.FC<ACHPaymentFormProps> = ({
  amount,
  packageId,
  scheduledOfferId,
  packageTitle,
  promoCode,
  promoBonusGC,
  promoBonusSC,
  onSuccess,
  onError,
  checkoutSessionId,
}) => {
  const isScheduledOffer = !!scheduledOfferId;
  const [bankData, setBankData] = useState({
    accountNumber: '',
    routingNumber: '',
    accountType: 'checking' as 'checking' | 'savings',
    accountHolderName: '',
  });
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const merchantId = import.meta.env.VITE_COINFLOW_MERCHANT_ID || '';
  const environment = (import.meta.env.VITE_COINFLOW_ENVIRONMENT || 'sandbox') as 'sandbox' | 'production';
  const tokenization = new CoinflowTokenization(merchantId, environment);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Step 1: Tokenize bank account (client-side)
      const token = await tokenization.tokenizeBankAccount({
        ...bankData,
        accountHolderName: `${billingInfo.firstName} ${billingInfo.lastName}`,
      });

      // Step 2: Submit checkout
      const response = await apiClient.post('/payments/checkout', {
        packageId: isScheduledOffer ? undefined : packageId,
        scheduledOfferId: isScheduledOffer ? scheduledOfferId : undefined,
        paymentMethod: 'ach',
        token, // ACH token from Coinflow
        customerInfo: billingInfo,
        deviceId: getDeviceId(),
        promoCode: promoCode || undefined,
        promoBonusGc: promoBonusGC || 0,
        promoBonusSc: promoBonusSC || 0,
        chargebackProtectionData: [
          {
            productName: 'package',
            productType: COINFLOW_PRODUCT_TYPE,
            quantity: 1,
            rawProductData: {
              description: COINFLOW_PRODUCT_DESCRIPTION,
              packageId,
              packageTitle,
              amount,
            },
          },
        ],
        checkoutSessionId: checkoutSessionId || undefined, // Reuse existing checkout session if available
      });

      if (response.externalPaymentId) {
        onSuccess(response.externalPaymentId, { isFirstPurchase: response.isFirstPurchase });
      } else {
        onError('Payment initiation failed');
      }
    } catch (error: any) {
      // Pass the full error object so error code can be extracted
      const errorObj = error.response?.error 
        ? new Error(error.response?.message || error.message || 'ACH payment failed')
        : error;
      (errorObj as any).code = error.response?.error || error.code;
      (errorObj as any).response = error.response;
      onError(errorObj);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2 lg:space-y-4">
      <Card className="bg-gradient-to-br from-card via-card/80 to-card/60 backdrop-blur-md border border-border shadow-lg">
        <CardHeader className="p-2.5 lg:p-6">
          <CardTitle className="flex items-center gap-1.5 lg:gap-2 text-[10px] lg:text-base font-black text-foreground uppercase tracking-wide">
            <Building2 className="h-3.5 w-3.5 lg:h-5 lg:w-5" />
            Bank Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 lg:space-y-4 p-2.5 lg:p-6 pt-0 lg:pt-0">
          {/* Account Holder Name */}
          <div>
            <Label htmlFor="accountHolderName" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Account Holder Name</Label>
            <Input
              id="accountHolderName"
              value={bankData.accountHolderName}
              onChange={(e) =>
                setBankData({ ...bankData, accountHolderName: e.target.value })
              }
              disabled={isProcessing}
              required
              className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
            />
          </div>

          {/* Account Type */}
          <div>
            <Label htmlFor="accountType" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Account Type</Label>
            <Select
              value={bankData.accountType}
              onValueChange={(value: 'checking' | 'savings') =>
                setBankData({ ...bankData, accountType: value })
              }
              disabled={isProcessing}
            >
              <SelectTrigger className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="checking">Checking</SelectItem>
                <SelectItem value="savings">Savings</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Routing Number */}
          <div>
            <Label htmlFor="routingNumber" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Routing Number</Label>
            <Input
              id="routingNumber"
              type="text"
              placeholder="123456789"
              value={bankData.routingNumber}
              onChange={(e) =>
                setBankData({ ...bankData, routingNumber: e.target.value })
              }
              maxLength={9}
              disabled={isProcessing}
              required
              className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
            />
          </div>

          {/* Account Number */}
          <div>
            <Label htmlFor="accountNumber" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Account Number</Label>
            <Input
              id="accountNumber"
              type="text"
              placeholder="0000000000"
              value={bankData.accountNumber}
              onChange={(e) =>
                setBankData({ ...bankData, accountNumber: e.target.value })
              }
              disabled={isProcessing}
              required
              className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-card via-card/80 to-card/60 backdrop-blur-md border border-border shadow-lg">
        <CardHeader className="p-2.5 lg:p-6">
          <CardTitle className="text-[10px] lg:text-base font-black text-foreground uppercase tracking-wide">Billing Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 lg:space-y-4 p-2.5 lg:p-6 pt-0 lg:pt-0">
          {/* Name */}
          <div className="grid grid-cols-2 gap-1.5 lg:gap-4">
            <div>
              <Label htmlFor="achFirstName" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">First Name</Label>
              <Input
                id="achFirstName"
                value={billingInfo.firstName}
                onChange={(e) =>
                  setBillingInfo({ ...billingInfo, firstName: e.target.value })
                }
                disabled={isProcessing}
                required
                className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
              />
            </div>
            <div>
              <Label htmlFor="achLastName" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Last Name</Label>
              <Input
                id="achLastName"
                value={billingInfo.lastName}
                onChange={(e) =>
                  setBillingInfo({ ...billingInfo, lastName: e.target.value })
                }
                disabled={isProcessing}
                required
                className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <Label htmlFor="achAddress" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">Address</Label>
            <Input
              id="achAddress"
              value={billingInfo.address}
              onChange={(e) =>
                setBillingInfo({ ...billingInfo, address: e.target.value })
              }
              disabled={isProcessing}
              required
              className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
            />
          </div>

          {/* City, State, ZIP */}
          <div className="grid grid-cols-3 gap-1.5 lg:gap-4">
            <div>
              <Label htmlFor="achCity" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">City</Label>
              <Input
                id="achCity"
                value={billingInfo.city}
                onChange={(e) =>
                  setBillingInfo({ ...billingInfo, city: e.target.value })
                }
                disabled={isProcessing}
                required
                className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
              />
            </div>
            <div>
              <Label htmlFor="achState" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">State</Label>
              <Input
                id="achState"
                value={billingInfo.state}
                onChange={(e) =>
                  setBillingInfo({ ...billingInfo, state: e.target.value })
                }
                disabled={isProcessing}
                required
                className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
              />
            </div>
            <div>
              <Label htmlFor="achZip" className="text-[10px] lg:text-sm font-black text-foreground uppercase tracking-wide">ZIP</Label>
              <Input
                id="achZip"
                value={billingInfo.zip}
                onChange={(e) =>
                  setBillingInfo({ ...billingInfo, zip: e.target.value })
                }
                disabled={isProcessing}
                required
                className="mt-1 h-7 lg:h-10 text-[10px] lg:text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ACH Terms Notice */}
      <div className="flex items-start gap-1.5 lg:gap-2 p-2 lg:p-3 bg-blue-500/20 border border-blue-400/30 rounded-lg">
        <Lock className="h-3 w-3 lg:h-4 lg:w-4 mt-0.5 text-blue-400 flex-shrink-0" />
        <div className="text-[9px] lg:text-xs text-blue-300/80">
          <p className="font-black mb-0.5 lg:mb-1">ACH Payment Terms:</p>
          <ul className="list-disc list-inside space-y-0.5 lg:space-y-1">
            <li>ACH payments typically take 2-3 business days to process</li>
            <li>Your bank account information is securely tokenized</li>
            <li>You authorize us to debit your account for this purchase</li>
          </ul>
        </div>
      </div>

      {/* Submit Button */}
      <Button 
        type="submit" 
        disabled={isProcessing} 
        className="w-full h-8 lg:h-11 bg-gradient-to-r from-green-600 via-emerald-500 to-green-600 hover:from-green-500 hover:via-green-600 hover:to-green-700 text-white font-black rounded-lg shadow-xl hover:shadow-2xl border border-green-400/30 hover:border-green-300/50 transition-all duration-200 text-[10px] lg:text-sm uppercase tracking-wide relative overflow-hidden group/btn"
      >
        {isProcessing ? (
          <span className="flex items-center gap-1.5 lg:gap-2">
            <span className="animate-spin">⏳</span>
            <span className="text-[9px] lg:text-sm">Processing...</span>
          </span>
        ) : (
          <span className="relative z-10">Initiate ACH Payment - ${(amount / 100).toFixed(2)}</span>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-1000"></div>
      </Button>
    </form>
  );
};

